import type { WeboEntry, WeboImport } from '../xml/importWebo';
import { catalogueKeyFor, exerciseFromVariant, loadCatalogue, variantIndex } from '../data/catalogue';
import { ageTier } from './disciplines';
import { cleanupDanglingReferences, findOrCreateClub, newAthlete, totalPoints, uid } from './workspace';
import { t } from '../i18n';
import type { Athlete, Catalogue, CatalogueKey, Competition, Exercise, FigureVariant, Programme, Starter, Workspace } from '../types';

/** Points sums are compared with this tolerance — matches the 2-decimal
 *  rounding `xmlNumber()` already applies to `Aufgestellt` on export. */
const POINTS_TOLERANCE = 0.05;

export interface WeboProgrammeResult {
  program: Exercise[];
  /** `Nr` values not found in the catalogue — skipped, never fabricated. */
  unmatchedNrs: readonly string[];
  /** The rebuilt programme's total points, computed here while every figure
   *  is still resolved — `Exercise` itself no longer carries points, see
   *  types.ts, so this is the only place that can cheaply recompute it. */
  rebuiltTotal: number;
  /** True if any figure was unmatched, or the rebuilt total doesn't match
   *  the file's own declared `Aufgestellt` within tolerance. */
  pointsMismatch: boolean;
}

/**
 * Rebuild a programme from figure numbers alone, looking each one up in
 * Kür's own catalogue rather than trusting the source file's own text/points
 * — the same `Exercise` a coach would get by picking the figure by hand in
 * `FigurePicker.tsx`. `declaredTotal` (the file's own `Aufgestellt`) is used
 * only as a sanity check, never to drive the import.
 *
 * Pure and synchronous — takes an already-loaded `Catalogue` rather than
 * fetching one itself, so it can be unit-tested directly against a
 * catalogue JSON file (`readFileSync`, no network) the same way
 * `test/fixtures/authKliehm.ts` already does. `resolveWeboProgrammes()`
 * below is the only caller that actually fetches (`loadCatalogue()`, which
 * needs a bundler-resolved `?url` import and so only works in the browser).
 */
export function buildWeboProgramme(nrs: readonly string[], catalogue: Catalogue, declaredTotal: number): WeboProgrammeResult {
  const index = variantIndex(catalogue);
  const program: Exercise[] = [];
  const unmatchedNrs: string[] = [];
  const resolved: FigureVariant[] = [];
  for (const nr of nrs) {
    const variant = index.get(nr);
    if (variant) {
      program.push(exerciseFromVariant(variant));
      resolved.push(variant);
    } else {
      unmatchedNrs.push(nr);
    }
  }
  const rebuiltTotal = totalPoints(resolved);
  const pointsMismatch = unmatchedNrs.length > 0 || Math.abs(rebuiltTotal - declaredTotal) > POINTS_TOLERANCE;
  return { program, unmatchedNrs, rebuiltTotal, pointsMismatch };
}

export type ResolvedWeboEntry = WeboEntry & { programmeResult: WeboProgrammeResult };
export type ResolvedWeboImport = Omit<WeboImport, 'entries'> & { entries: ResolvedWeboEntry[] };

/** Fetches whichever catalogues the file's entries actually need (each
 *  fetched once, even if several entries share a type — `loadCatalogue()`
 *  itself also caches per key), then runs `buildWeboProgramme()` for every
 *  entry — not just ones the coach ends up selecting, since catalogue
 *  fetches are cheap/cached and knowing which entries have unmatched
 *  figures or a points mismatch is exactly what a coach needs to see before
 *  deciding what to import. */
export async function resolveWeboProgrammes(imp: WeboImport): Promise<ResolvedWeboImport> {
  const neededKeys = [...new Set(imp.entries.map((e) => catalogueKeyFor(e.starter.type)))];
  const loaded = await Promise.all(neededKeys.map((key) => loadCatalogue(key)));
  const catalogues = new Map<CatalogueKey, Catalogue>(neededKeys.map((key, i) => [key, loaded[i]!]));

  const entries = imp.entries.map((entry) => {
    const catalogue = catalogues.get(catalogueKeyFor(entry.starter.type))!;
    return { ...entry, programmeResult: buildWeboProgramme(entry.figureNrs, catalogue, entry.declaredTotal) };
  });
  return { ...imp, entries };
}

export interface WeboSelection {
  /** Contact person + registering club + Landesverband — overwrites
   *  `workspace.contact`/`meta.club`/`meta.lv`/`meta.federation`. */
  registrationData: boolean;
  /** Always `false` when `imp.competition` is `null`. */
  competition: boolean;
  /** `WeboEntry.id`s contributing their `Starter` (+ athletes/clubs). */
  starterIds: Set<string>;
  /** `WeboEntry.id`s contributing their rebuilt `Programme`, independent of
   *  `starterIds` — a coach can import a routine without its starter, or a
   *  starter without a routine yet. Always empty for teammeldung, which
   *  carries no figure data to rebuild a programme from. */
  programIds: Set<string>;
}

export function fullWeboSelection(imp: WeboImport): WeboSelection {
  const canProgramme = imp.schema === 'kunstradmeldung';
  return {
    registrationData: imp.contact !== null,
    competition: imp.competition !== null,
    starterIds: new Set(imp.entries.map((e) => e.id)),
    programIds: canProgramme ? new Set(imp.entries.map((e) => e.id)) : new Set(),
  };
}

export interface WeboTreeItem {
  readonly id: string;
  readonly label: string;
  /** Only meaningful for a Programme leaf. */
  readonly mismatch?: boolean;
}

export function weboTreeItems(resolved: ResolvedWeboImport): { starters: WeboTreeItem[]; programs: WeboTreeItem[] } {
  const starters = resolved.entries.map((e) => ({ id: e.id, label: e.label }));
  const programs = resolved.entries.map((e) => {
    const r = e.programmeResult;
    const detail = r.pointsMismatch
      ? t('webo.item.programme.mismatch', {
          count: r.program.length,
          unmatched: r.unmatchedNrs.length,
          rebuilt: r.rebuiltTotal.toFixed(2),
          declared: e.declaredTotal.toFixed(2),
        })
      : t('webo.item.programme.ok', { count: r.program.length, points: r.rebuiltTotal.toFixed(2) });
    return { id: e.id, label: `${e.label} — ${detail}`, mismatch: r.pointsMismatch };
  });
  return { starters, programs };
}

export interface WeboImportResult {
  workspace: Workspace;
  created: { comps: number; starters: number; programs: number; athletes: number; clubs: number; entries: number };
  /** Matched an existing record instead of duplicating it. */
  reused: { athletes: number; clubs: number };
  /** Of the reused athletes, how many actually had a previously-blank field
   *  (UCI-ID, address, club, ...) filled in from the import — see
   *  `backfillAthlete()`. */
  updated: { athletes: number };
}

function matchAthlete(existing: readonly Athlete[], candidate: Athlete): Athlete | undefined {
  if (candidate.uciId.trim() !== '') {
    const byUci = existing.find((a) => a.uciId.trim() !== '' && a.uciId.trim() === candidate.uciId.trim());
    if (byUci) return byUci;
  }
  return existing.find(
    (a) =>
      a.firstName.trim() === candidate.firstName.trim() &&
      a.lastName.trim() === candidate.lastName.trim() &&
      // A blank birthdate on either side doesn't block the match — a coach
      // often enters an athlete by hand with just a name before the WeBo
      // file (or the reverse) fills the rest in. Two different non-blank
      // birthdates, though, means two different people with the same name.
      (a.birthDate === '' || candidate.birthDate === '' || a.birthDate === candidate.birthDate),
  );
}

const BACKFILLABLE_FIELDS = ['uciId', 'birthDate', 'nation', 'countryCode', 'postalCode', 'city', 'street', 'phone', 'fax', 'email'] as const;

/**
 * Fill in `existing`'s blank fields from `candidate` — never overwrites a
 * field that already has a value, so a coach's own (possibly deliberately
 * different) data always wins. `firstName`/`lastName`/`sex` are never
 * touched: the first two are part of the matching key already, and
 * `sex` has no "unset" state to fill. Mutates `existing` in place;
 * returns whether anything actually changed, for the import summary.
 */
function backfillAthlete(existing: Athlete, candidate: Athlete, clubId: string | undefined): boolean {
  let changed = false;
  for (const field of BACKFILLABLE_FIELDS) {
    if (existing[field].trim() === '' && candidate[field].trim() !== '') {
      existing[field] = candidate[field];
      changed = true;
    }
  }
  if (existing.clubId === '' && clubId !== undefined) {
    existing.clubId = clubId;
    changed = true;
  }
  return changed;
}

function buildProgramme(entry: ResolvedWeboEntry): Programme {
  return {
    id: uid(),
    type: entry.starter.type,
    ageTier: ageTier(entry.starter.discipline),
    name: '',
    program: entry.programmeResult.program,
  };
}

/**
 * Merge a resolved WeBo import into `current`, honoring `sel`. Every list
 * category is additive (new records only) except `registrationData`, which
 * **overwrites** `workspace.contact`/`meta` — the one deliberate exception,
 * since a contact/registering-club is a singleton fact, not a list to merge.
 * Athletes are matched against the workspace as it's built up so far by
 * UCI-ID (falling back to name+birthdate), and clubs by exact name — the
 * same heuristic `parseWorkspace()` already uses for legacy free-text club
 * migration (`findOrCreateClub()`) — so re-importing the same file, or a
 * second file for the same people, doesn't pile up duplicates. A matched
 * athlete also gets any of its blank fields (UCI-ID, address, club, ...)
 * filled in from the import (`backfillAthlete()`) — the common case a
 * coach hits when an athlete was first entered by hand with only a name,
 * then a WeBo file for that same person turns up with the rest of the data.
 */
export function applyWeboImport(current: Workspace, resolved: ResolvedWeboImport, sel: WeboSelection): WeboImportResult {
  const workspace: Workspace = {
    ...current,
    comps: [...current.comps],
    clubs: [...current.clubs],
    athletes: [...current.athletes],
    starters: [...current.starters],
    programs: [...current.programs],
    entries: [...current.entries],
  };

  const created = { comps: 0, starters: 0, programs: 0, athletes: 0, clubs: 0, entries: 0 };
  const reused = { athletes: 0, clubs: 0 };
  const updated = { athletes: 0 };

  if (sel.registrationData && resolved.contact) {
    workspace.contact = resolved.contact;
    if (resolved.meta) {
      workspace.meta = { ...workspace.meta, club: resolved.meta.club, lv: resolved.meta.lv };
      if (resolved.meta.federation !== '') workspace.meta.federation = resolved.meta.federation;
    }
  }

  let newCompId = '';
  if (sel.competition && resolved.competition) {
    const comp: Competition = { ...resolved.competition, id: uid() };
    workspace.comps.push(comp);
    newCompId = comp.id;
    created.comps++;
  }

  for (const entry of resolved.entries) {
    const wantStarter = sel.starterIds.has(entry.id);
    const wantProgramme = sel.programIds.has(entry.id);
    if (!wantStarter && !wantProgramme) continue;

    let starterId = '';
    if (wantStarter) {
      const clubIdByName = new Map<string, string>();
      for (const name of entry.clubNames) {
        const before = workspace.clubs.length;
        const club = findOrCreateClub(workspace.clubs, name);
        if (workspace.clubs.length > before) created.clubs++;
        else reused.clubs++;
        clubIdByName.set(name, club.id);
      }

      const resolvedAthleteIds = entry.athletes.map((candidate, i) => {
        const clubName = entry.clubNames[i];
        const clubId = clubName !== undefined ? clubIdByName.get(clubName) : undefined;
        const match = matchAthlete(workspace.athletes, candidate);
        if (match) {
          reused.athletes++;
          if (backfillAthlete(match, candidate, clubId)) updated.athletes++;
          return match.id;
        }
        const athlete: Athlete = { ...newAthlete(), ...candidate, id: uid() };
        if (clubId !== undefined) athlete.clubId = clubId;
        workspace.athletes.push(athlete);
        created.athletes++;
        return athlete.id;
      });

      const starter: Starter = { ...entry.starter, id: uid(), athleteIds: resolvedAthleteIds };
      workspace.starters.push(starter);
      created.starters++;
      starterId = starter.id;
    }

    let programId = '';
    if (wantProgramme) {
      const programme = buildProgramme(entry);
      workspace.programs.push(programme);
      created.programs++;
      programId = programme.id;
    }

    if (wantStarter && wantProgramme) {
      workspace.entries.push({ id: uid(), compId: newCompId, starterId, programId });
      created.entries++;
    }
  }

  cleanupDanglingReferences(workspace);

  return { workspace, created, reused, updated };
}
